import React from 'react';
import {NewLeagueEventMatch} from "../domain/leagueevent/models";
import {Player} from "../domain/player/models";

interface Props {
    matches: NewLeagueEventMatch[]
}

type MatchResult = 'win' | 'loss' | 'skip' | null;

interface MatchCell {
    result: MatchResult
    ratingBefore?: number
    ratingAfter?: number
}

interface GroupMatrixProps {
    groupNumber: number
    matches: NewLeagueEventMatch[]
}

export function ResultsMatrix({matches}: Props): JSX.Element {
    const groupNumbers = Array.from(new Set(matches.map(m => m.groupNumber))).sort((a, b) => a - b);

    return (
        <div className="results-matrix">
            {groupNumbers.map(groupNumber => (
                <GroupMatrix
                    key={groupNumber}
                    groupNumber={groupNumber}
                    matches={matches.filter(m => m.groupNumber === groupNumber)}
                />
            ))}
        </div>
    );
}

function GroupMatrix({groupNumber, matches}: GroupMatrixProps): JSX.Element {
    const playerMap = new Map<number, Player>();
    matches.forEach(m => m.players?.forEach(p => playerMap.set(Number(p.id), p)));
    const players = Array.from(playerMap.values());

    function getCell(rowPlayer: Player, colPlayer: Player): MatchCell {
        const rowId = Number(rowPlayer.id);
        const colId = Number(colPlayer.id);
        const match = matches.find(m =>
            m.players?.some(p => Number(p.id) === rowId) &&
            m.players?.some(p => Number(p.id) === colId)
        );
        if (!match) return {result: null};
        if (match.winnerId === 0) return {result: 'skip'};

        const isWinner = Number(match.winnerId) === rowId;
        const showRating = !!match.match && match.match.winnerRatingBefore !== 0;
        if (!showRating) {
            return {result: isWinner ? 'win' : 'loss'};
        }
        return {
            result: isWinner ? 'win' : 'loss',
            ratingBefore: isWinner ? match.match!.winnerRatingBefore : match.match!.loserRatingBefore,
            ratingAfter: isWinner ? match.match!.winnerRatingAfter : match.match!.loserRatingAfter,
        };
    }

    function cellClass(result: MatchResult): string {
        switch (result) {
            case 'win': return 'matrix-cell-win';
            case 'loss': return 'matrix-cell-loss';
            case 'skip': return 'matrix-cell-skip';
            default: return 'matrix-cell-empty';
        }
    }

    function cellLabel(result: MatchResult): string | null {
        switch (result) {
            case 'win': return 'W';
            case 'loss': return 'L';
            case 'skip': return '—';
            default: return null;
        }
    }

    function ratingDelta(cell: MatchCell): number | null {
        if (cell.ratingBefore === undefined || cell.ratingAfter === undefined) return null;
        return cell.ratingAfter - cell.ratingBefore;
    }

    return (
        <div className="matrix-group">
            <h4 className="matrix-group-title">Group {groupNumber}</h4>
            <div className="matrix-table-wrapper">
                <table className="matrix-table">
                    <thead>
                    <tr>
                        <th className="matrix-corner"/>
                        {players.map(p => (
                            <th key={Number(p.id)} className="matrix-col-header">
                                {p.firstName}<br/>{p.lastName}
                            </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {players.map(rowPlayer => (
                        <tr key={Number(rowPlayer.id)}>
                            <th className="matrix-row-header">
                                {rowPlayer.firstName} {rowPlayer.lastName}
                            </th>
                            {players.map(colPlayer => {
                                const colId = Number(colPlayer.id);
                                if (Number(rowPlayer.id) === colId) {
                                    return <td key={colId} className="matrix-cell matrix-cell-self"/>;
                                }
                                const cell = getCell(rowPlayer, colPlayer);
                                const label = cellLabel(cell.result);
                                const delta = ratingDelta(cell);
                                return (
                                    <td key={colId} className={`matrix-cell ${cellClass(cell.result)}`}>
                                        {label && <div className="matrix-cell-label">{label}</div>}
                                        {delta !== null && (
                                            <div className="matrix-cell-delta">
                                                {delta > 0 ? `+${delta}` : delta}
                                            </div>
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
